<script setup lang="ts">
import ReportParameterField from './ReportParameterField.vue'
import type { ReportParameter, ReportParameterValues, ReportRow, ReportScalar } from '@/modules/reportTemplates/api'
import { emptyReportRow } from '@/modules/reportTemplates/parameters'

const props = defineProps<{ fields: ReportParameter[]; modelValue: ReportParameterValues; errors: Record<string, string>; disabled?: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [value: ReportParameterValues] }>()
const rows = (key: string): ReportRow[] => Array.isArray(props.modelValue[key]) ? props.modelValue[key] as ReportRow[] : []
const scalar = (key: string): ReportScalar => Array.isArray(props.modelValue[key]) ? null : props.modelValue[key] as ReportScalar
function update(key: string, value: ReportScalar | ReportRow[]) {
  emit('update:modelValue', { ...props.modelValue, [key]: value })
}
function cell(key: string, index: number, column: string, value: ReportScalar) {
  update(key, rows(key).map((row, i) => i === index ? { ...row, [column]: value } : row))
}
</script>

<template>
  <VRow>
    <VCol
      v-for="field in fields"
      :key="field.key"
      cols="12"
      :sm="['text', 'boolean', 'table'].includes(field.type) ? 12 : 6"
    >
      <section v-if="field.type === 'table'" class="report-rows">
        <h4 class="text-subtitle-1">
          {{ field.label }}{{ field.required ? ' *' : '' }}
        </h4>
        <p v-if="field.hint" class="text-body-2 text-medium-emphasis">
          {{ field.hint }}
        </p>
        <p v-if="errors[field.key]" class="text-error" role="alert">
          {{ errors[field.key] }}
        </p>
        <VCard
          v-for="(row, index) in rows(field.key)"
          :key="index"
          variant="outlined"
          class="pa-4 mt-3"
        >
          <div class="d-flex align-center justify-space-between mb-3">
            <span class="text-subtitle-2">Запись {{ index + 1 }}</span>
            <VBtn
              icon="ri-delete-bin-line"
              size="small"
              variant="text"
              :disabled="disabled"
              :aria-label="`Удалить запись ${index + 1}: ${field.label}`"
              @click="update(field.key, rows(field.key).filter((_, i) => i !== index))"
            />
          </div>
          <VRow>
            <VCol
              v-for="column in field.columns"
              :key="column.key"
              cols="12"
              :sm="column.type === 'text' ? 12 : 6"
            >
              <ReportParameterField
                :field="column"
                :model-value="row[column.key]"
                :disabled="disabled"
                :error="errors[`${field.key}.${index}.${column.key}`]"
                @update:model-value="cell(field.key, index, column.key, $event)"
              />
            </VCol>
          </VRow>
        </VCard>
        <VBtn
          class="mt-3"
          variant="tonal"
          prepend-icon="ri-add-line"
          :disabled="disabled || rows(field.key).length >= (field.max_rows || 30)"
          @click="update(field.key, [...rows(field.key), emptyReportRow(field.columns || [])])"
        >
          Добавить запись
        </VBtn>
        <span class="text-caption ml-3">{{ rows(field.key).length }} / {{ field.max_rows || 30 }}</span>
      </section>
      <ReportParameterField
        v-else
        :field="field"
        :model-value="scalar(field.key)"
        :disabled="disabled"
        :error="errors[field.key]"
        @update:model-value="update(field.key, $event)"
      />
    </VCol>
  </VRow>
</template>
